import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex } from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import { useCustomToast } from "components/CustomToast/CustomToast";
import Loading from "components/Loading/Loading";
import SearchTable2 from "components/Tables/SearchTable2";
import { QualitativeViolationsFilter } from "./QualitativeViolationsFilter";
import { getEmptyPageResponse, toTablePageableData } from "utils";
import { reportsService } from "services/reportsService";
import { ViolationStatus } from "services/trafficViolationService";

export const QualitativeViolationsList = () => {
    const { t } = useTranslation();
    const showToast = useCustomToast();
    const [searchParams, setSearchParams] = useState({ orderBy: "data_infracao" });
    const [response, setResponse] = useState(undefined);

    const columns = useMemo(
        () => [
            {
                Header: t("reports.violations-qualitative.list.table.header.ait-number"),
                accessor: "numero_ait",
            },
            {
                Header: t("reports.violations-qualitative.list.table.header.ait-vehicle-owner-name"),
                accessor: "nome_proprietario_veiculo_ait",
            },
            {
                Header: t("reports.violations-qualitative.list.table.header.vehicle-plate"),
                accessor: "placa_veiculo",
            },
            {
                Header: t("reports.violations-qualitative.list.table.header.infringement-date"),
                accessor: "data_infracao",
            },
            {
                Header: t("reports.violations-qualitative.list.table.header.infringement"),
                accessor: "infracao",
            },
            {
                Header: t("reports.violations-qualitative.list.table.header.street"),
                accessor: "logradouro_local_infracao",
            },
            {
                Header: t("reports.violations-qualitative.list.table.header.agent"),
                accessor: "descricao_agente",
            },
            {
                Header: t("reports.violations-qualitative.list.table.header.value"),
                accessor: "valor",
            },
            {
                Header: t("reports.violations-qualitative.list.table.header.payment"),
                accessor: "pagamento",
            },
            {
                Header: t("reports.violations-qualitative.list.table.header.validation"),
                accessor: "status",
            },
        ],
        [t]
    );

    const fetchData = async (params = {}) => {
        params.orderBy = params.orderBy ?? "data_infracao";
        params.orderDirection = params.orderDirection ?? "desc";
        params.page = Math.min(params?.page ?? 1, response?.total_pages ?? Infinity) || 1;
        if (response) setResponse(undefined);

        try {
            params = { ...searchParams, ...params };
            setSearchParams(params);
            const data = await reportsService.list(params);
            setResponse(data);
        } catch (err) {
            setResponse(getEmptyPageResponse(searchParams));
            showToast({ title: t(err), status: "error" });
        }
    };

    useEffect(() => {
        fetchData({ filters: { status: ViolationStatus.VALIDATED.value } });
    }, []);

    return (
        <Flex direction="column" gap="5" pt={{ base: "150px", lg: "90px" }}>
            <Flex w="100%" px="30px" justify="flex-end" align="center" gap="4" direction={{ sm: "column", md: "row" }}>
                <QualitativeViolationsFilter onFilter={(filters) => fetchData({ filters, page: 1 })} />
            </Flex>
            <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                <ListPageCardHeader title={t("reports.violations-qualitative.title")} subtitle={t("reports.violations-qualitative.description")} />
                <CardBody>
                    {response ? (
                        <SearchTable2
                            columnsData={columns}
                            tableData={response.content}
                            pageableData={toTablePageableData(response, { defaultField: "data_infracao", ...searchParams })}
                            onChange={fetchData}
                            disableSearch={true}
                        />
                    ) : (
                        <Loading maxH={{ sm: "100px", md: "350px" }} />
                    )}
                </CardBody>
            </Card>
        </Flex>
    );
};

export default QualitativeViolationsList;